/** tm/legal/LegalNewsPopup.tsx — 업무관리 어느 화면에서든 뜨는 「새 소식」 카드 (데스크톱 오른쪽 아래 · 모바일 아래쪽 시트).
 *  중요한 순서로 최대 3건 + 한 줄 미끼 문구, 「바로 보기」·「전체 보기」·「나중에」. 12초 뒤 저절로 닫힘 (마우스·포커스가 있으면 멈춤). */
import { useEffect, useRef, useState } from 'react';
import { C, T, R } from '../components/ui';
import { btn, MUTED } from './legalTheme';
import { SourceBadge, TopicTag, ItemTitle } from './legalUi';
import { badgeOf, hookOf, hookColor, navOf, type AlertEntry } from './alertUtil';
import { reducedMotion } from './alertPrefs';
import { useNewsPopup } from './useNewsPopup';
import type { LegalAlertsState } from './useLegalAlerts';
import type { LegalNavParams } from './legalTypes';

const AUTO_HIDE_MS = 12000;

interface Props { alerts: LegalAlertsState; userId: string; mobile: boolean; onOpen: (p: LegalNavParams) => void }

/** 셸 전역 스타일 — 배지 깜빡임 · 팝업 등장 (동작 줄이기면 끔) */
export function LegalShellStyle() {
  return <style>{`
@keyframes lw-pulse{0%{transform:scale(1);box-shadow:0 0 0 0 rgba(220,38,38,.55)}40%{transform:scale(1.25);box-shadow:0 0 0 6px rgba(220,38,38,0)}100%{transform:scale(1);box-shadow:0 0 0 0 rgba(220,38,38,0)}}
.lw-pulse{animation:lw-pulse .9s ease-out 2}
@keyframes lw-up{from{transform:translateY(24px);opacity:0}to{transform:none;opacity:1}}
.lw-pop{animation:lw-up .28s ease-out}
@keyframes lw-bar{from{transform:scaleX(1)}to{transform:scaleX(0)}}
.lw-pop-row:hover{background:${C.bgAlt}}
.lw-pop-row:focus-visible,.lw-pop button:focus-visible{outline:2px solid ${C.accent};outline-offset:-2px}
@media (prefers-reduced-motion:reduce){.lw-pulse,.lw-pop{animation:none}}
`}</style>;
}

function Row({ e, first, compact, onOpen }: { e: AlertEntry; first: boolean; compact?: boolean; onOpen: () => void }) {
  const b = badgeOf(e);
  return (
    <button type="button" className="lw-pop-row" onClick={onOpen} style={{
      display: 'block', width: '100%', textAlign: 'left', border: 0, borderTop: `1px solid ${C.bg}`, background: first && !compact ? C.bgAlt : 'transparent',
      padding: compact ? '6px 16px 8px' : '10px 16px', cursor: 'pointer', fontFamily: 'inherit', minHeight: 44,
    }}>
      <span style={{ display: compact ? 'none' : 'flex', gap: 5, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
        <SourceBadge group={b.group} label={b.label} />
        {e.it.topic && <TopicTag>{e.it.topic}</TopicTag>}
      </span>
      <span className={compact ? undefined : 'lw-clamp'} style={{ display: 'block', fontSize: 14, fontWeight: 700, color: C.text, lineHeight: 1.45, ...(compact ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } : {}) }}><ItemTitle title={e.it.title} custom={e.it.custom_title} /></span>
      <span style={{ display: 'block', fontSize: T.sm, color: hookColor(e, C.accent, C.sub), marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{hookOf(e)}</span>
    </button>
  );
}

export default function LegalNewsPopup({ alerts, userId, mobile, onOpen }: Props) {
  const { pop, close, later } = useNewsPopup(userId, alerts.data, onOpen);
  const [paused, setPaused] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const still = reducedMotion();

  useEffect(() => {
    if (!pop || paused) return undefined;
    const t = setTimeout(() => close(true), AUTO_HIDE_MS);
    return () => clearTimeout(t);
  }, [pop, paused, close]);
  useEffect(() => { if (!pop) setPaused(false); }, [pop]);

  if (!pop) return <LegalShellStyle />;
  const go = (p: LegalNavParams) => { close(true); onOpen(p); };
  const others = alerts.total - pop.fresh;
  // 모바일: 화면 30% 이하 — 1건 + "n건 더"
  const shown = mobile ? pop.list.slice(0, 1) : pop.list;
  const more = pop.fresh - shown.length;

  return <>
    <LegalShellStyle />
    <div ref={box} role="region" aria-label="새 소식 알림" aria-live="polite" className="lw-pop"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => { if (!box.current?.contains(document.activeElement)) setPaused(false); }}
      onFocus={() => setPaused(true)} onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setPaused(false); }}
      onKeyDown={e => { if (e.key === 'Escape') close(true); }}
      style={{
        position: 'fixed', zIndex: 1300, background: C.white, overflow: 'hidden', border: `1px solid ${C.border}`,
        boxShadow: '0 16px 40px rgba(17,24,39,.22)', fontFamily: 'inherit',
        ...(mobile ? { left: 0, right: 0, bottom: 0, maxHeight: '30vh', borderRadius: `${R.lg}px ${R.lg}px 0 0`, paddingBottom: 'env(safe-area-inset-bottom)' }
          : { right: 20, bottom: 20, width: 372, borderRadius: 12 }),
      }}>
      <div aria-hidden="true" style={{ height: 3, background: C.bg }}>
        {!paused && !still && <div key={pop.latest} style={{ height: 3, background: C.accent, transformOrigin: 'left', animation: `lw-bar ${AUTO_HIDE_MS}ms linear forwards` }} />}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: mobile ? '2px 16px 0' : '12px 16px 10px' }}>
        <span aria-hidden="true" style={{ fontSize: 16 }}>🔔</span>
        <b style={{ fontSize: T.lg, color: C.text }}>새 소식 <span style={{ color: C.red, fontVariantNumeric: 'tabular-nums' }}>{pop.fresh}건</span></b>
        {mobile ? more > 0 && <span style={{ fontSize: T.sm, color: MUTED }}>· 이 밖에 {more}건 더</span>
          : others > 0 && <span style={{ fontSize: T.sm, color: MUTED }}>· 확인 전 {alerts.total}건</span>}
        <button type="button" onClick={() => close(true)} aria-label="닫기" style={{
          marginLeft: 'auto', border: 0, background: 'transparent', color: MUTED, fontSize: 20, cursor: 'pointer', width: 40, height: 40, marginRight: -10, borderRadius: R.sm,
        }}>×</button>
      </div>
      {shown.map((e, i) => <Row key={`${e.key}${e.it.id}`} e={e} first={i === 0} compact={mobile} onOpen={() => go(navOf(e))} />)}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: mobile ? '6px 12px 8px' : '10px 16px 12px', borderTop: `1px solid ${C.border}`, background: C.bgAlt }}>
        <button type="button" onClick={() => go(navOf(pop.list[0]))} style={{ ...btn('pri'), minHeight: mobile ? 40 : 32, flex: mobile ? 1 : undefined, justifyContent: 'center' }}>바로 보기</button>
        <button type="button" onClick={() => go({ topic: '', alerts: true })} style={{ ...btn(), minHeight: mobile ? 40 : 32, flex: mobile ? 1 : undefined, justifyContent: 'center' }}>전체 보기</button>
        <button type="button" onClick={later} title="2시간 뒤 다시 알려 드립니다" style={{ ...btn(), border: 0, background: 'transparent', marginLeft: mobile ? 0 : 'auto', minHeight: mobile ? 40 : 32 }}>나중에</button>
      </div>
    </div>
  </>;
}
